import React from 'react';

import {TableRow, TableBody, TableCell, Chip} from '@material-ui/core';

import {format, parse} from 'date-fns';

import {TableViewBodyProps} from './table-view-body.types';
import DeleteSharpIcon from '@material-ui/icons/DeleteSharp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import SendIcon from '@material-ui/icons/Send';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import GavelIcon from '@material-ui/icons/Gavel';

export const TableViewBody = (props: TableViewBodyProps) => {
  const {data, columns, actions} = props;

  const styleColorForRow = (additionFunc: any, row: any) => {
    return additionFunc ? {backgroundColor: additionFunc(row)} : {};
  };

  const formatDate = (dateString: string) => {
    try {
      // Remove timezone before parsing
      const parts = dateString.split(' ');
      const dateTime = parts.slice(0, -1).join(' ');
      const timezone = parts[parts.length - 1];

      // Parse the date
      const [datePart, timePart] = dateTime.split(' ');
      const [month, day, year] = datePart.split('/');
      const [time, period] = timePart.split(' ');
      const [hours, minutes] = time.split(':');

      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        period === 'PM' ? parseInt(hours) + 12 : parseInt(hours),
        parseInt(minutes),
      );

      // Format the date
      const formatter = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      return (
        formatter.format(date).replace(',', '').replace(',', ' at') +
        ' ' +
        timezone
      );
    } catch (error) {
      return dateString;
    }
  };

  const renderStatusIcon = (status: string) => {
    const iconStyle = {fontSize: 20};

    switch (status) {
      case 'Canceled':
        return <DeleteSharpIcon style={{...iconStyle, color: '#ff4d4f'}} />;
      case 'Delivered':
        return <CheckCircleIcon style={{...iconStyle, color: '#52c41a'}} />;
      case 'In Transit':
        return <LocalShippingIcon style={{...iconStyle, color: '#1890ff'}} />;
      case 'Pending Delivery Confirmation':
        return <AccessTimeIcon style={{...iconStyle, color: '#faad14'}} />;
      case 'Created':
        return <AddCircleIcon style={{...iconStyle, color: '#722ed1'}} />;
      case 'Ready for pickup':
        return (
          <LocalShippingOutlinedIcon style={{...iconStyle, color: '#1890ff'}} />
        );
      case 'Dispatched':
        return <SendIcon style={{...iconStyle, color: '#1890ff'}} />;
      case 'Confirmed':
        return (
          <AssignmentTurnedInIcon style={{...iconStyle, color: '#52c41a'}} />
        );
      case 'Bid Accepted':
        return <HowToRegIcon style={{...iconStyle, color: '#52c41a'}} />;
      case 'Carrier Confirmed':
        return (
          <AssignmentTurnedInIcon style={{...iconStyle, color: '#52c41a'}} />
        );
      case 'Bid Submitted':
        return <GavelIcon style={{...iconStyle, color: '#faad14'}} />;
      default:
        return <AccessTimeIcon style={{...iconStyle, color: '#faad14'}} />;
    }
  };

  const renderStatusColor = (status: string) => {
    switch (status) {
      case 'Canceled':
        return '#ffd6d6';
      case 'Delivered':
        return '#d3f9d8';
      case 'In Transit':
        return '#d0ebff';
      case 'Pending Delivery Confirmation':
        return '#ffe8cc';
      case 'Created':
        return '#eebefa';
      case 'Ready for pickup':
        return '#d0ebff';
      case 'Dispatched':
        return '#d0ebff';
      case 'Confirmed':
        return '#d3f9d8';
      case 'Bid Accepted':
        return '#d3f9d8';
      case 'Carrier Confirmed':
        return '#d3f9d8';
      case 'Bid Submitted':
        return '#ffe8cc';
      default:
        return '';
    }
  };

  return (
    <>
      <TableBody>
        {data.map((row, index) => {
          const rowFunctions =
            row['status'] === 'Canceled'
              ? row['actions'].slice(2)
              : row['actions'];

          return (
            <TableRow hover tabIndex={-1} key={index}>
              {columns.map((col, index) => {
                if (col.key === 'status') {
                  return (
                    <TableCell key={index}>
                      <Chip
                        icon={renderStatusIcon(row['status'])}
                        style={{
                          color: '#333',
                          backgroundColor: renderStatusColor(row['status']),
                        }}
                        label={
                          col.itemRow
                            ? col.itemRow(row)
                            : col.item
                            ? col.item(row[col.key])
                            : row[col.key]
                        }
                      />
                    </TableCell>
                  );
                }

                if (col.key === 'loads.expiration') {
                  return (
                    <TableCell key={index} align='left'>
                      {formatDate(row[col.key])}
                    </TableCell>
                  );
                }

                if (col.key === 'Pickup time') {
                  return (
                    <TableCell key={index} align='left'>
                      {formatDate(row[col.key])}
                    </TableCell>
                  );
                }

                if (col.key === 'loads.guid') {
                  return (
                    <TableCell
                      key={index}
                      align='left'
                      style={{fontSize: 12, color: 'gray'}}>
                      {row[col.key]}
                    </TableCell>
                  );
                }

                return (
                  <TableCell key={index} align='left'>
                    {col.itemRow
                      ? col.itemRow(row)
                      : col.item
                      ? col.item(row[col.key])
                      : row[col.key]}
                  </TableCell>
                );
              })}
              {rowFunctions && rowFunctions.length > 0 && (
                <TableCell
                  key={row.length}
                  align='left'
                  style={
                    rowFunctions.length > 2
                      ? {
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 10,
                        }
                      : {}
                  }>
                  {rowFunctions.map((actionId: string) => {
                    const act = actions.filter(
                      (action) => action.id === actionId,
                    )[0];

                    if (act.item.props.children === 'Active Bids') {
                      const item = {
                        ...act.item,
                        props: {
                          ...act.item.props,
                          style: {color: 'white', cursor: 'pointer'},
                          children:
                            act.item.props.children +
                            (act.nameAdditionFunc
                              ? act.nameAdditionFunc(row)
                              : ''),
                          onClick: () => {
                            act.action(row);
                          },
                        },
                      };
                      return (
                        <span style={{color: 'white', cursor: 'pointer'}}>
                          {item}
                        </span>
                      );
                    }

                    const item = {
                      ...act.item,
                      props: {
                        ...act.item.props,
                        style: styleColorForRow(act.colorAdditionFunc, row),
                        children:
                          act.item.props.children +
                          (act.nameAdditionFunc
                            ? act.nameAdditionFunc(row)
                            : ''),
                        onClick: () => {
                          act.action(row);
                        },
                      },
                    };

                    return (
                      <span key={actionId} style={{marginRight: 5}}>
                        {item}
                      </span>
                    );
                  })}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </>
  );
};
